import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { getImageUrl } from 'lib/image/imageUtils'
import { capitalise } from 'lib/string/stringUtils'

// Image ID for the discover modal animated banner
const DISCOVER_MODAL_IMAGE_ID = 'wrapped-cards-animation'

const titleContainerStyle = { minWidth: 0 }

const HeaderGroup = styled(Group)`
  padding: ${rem(24)} ${rem(24)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(168)};
  overflow: hidden;
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.default.one};

  ${mediaQueryUp.tablet} {
    height: ${rem(200)};
    padding-bottom: ${rem(16)};
  }
`

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

interface Props {
  /** User's first name for personalized greeting */
  firstName: string
}

function WrappedDiscoverModal({ firstName }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const navigateTo = useNavigateTo()

  const handleClose = useCallback(() => {
    modalElement?.resolve()
  }, [modalElement])

  const handleDiscover = useCallback(() => {
    // Close this modal first
    modalElement?.resolve()
    // Navigate to the wrapped page
    navigateTo('/le-wrapped-2025')
  }, [modalElement, navigateTo])

  const bannerImageUrl = getImageUrl(DISCOVER_MODAL_IMAGE_ID, {
    width: 800,
    quality: 'good',
    format: 'gif',
  })

  const name = capitalise(firstName?.trim() || intl.formatMessage({ id: 'aNu/DoYUMw', defaultMessage: 'Traveller', description: 'Fallback name when user first name is not available' }))

  return (
    <ModalBase size="S" mode="default">
      <HeaderGroup
        direction="horizontal"
        gap={12}
        verticalAlign="start"
        horizontalAlign="space-between"
      >
        <Group direction="vertical" grow style={titleContainerStyle}>
          <Heading variant="heading3" colour="neutral-eight">
            {intl.formatMessage({ id: 'o5bqL0LIga', defaultMessage: '{name}, your Year of Escapes 2025 is here!', description: 'Personalized heading for Wrapped discover modal' }, { name })}
          </Heading>
        </Group>
        <Group direction="vertical" shrink={false}>
          <IconButton
            kind="tertiary"
            variant="ghost"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: 'oJIpfUmUvq', defaultMessage: 'Close', description: 'Close button aria-label' })}
          >
            <LineTimesIcon />
          </IconButton>
        </Group>
      </HeaderGroup>
      <ModalBody>
        <ImageContainer>
          <BannerImage
            src={bannerImageUrl}
            alt={intl.formatMessage({ id: 'YGYRpdZknv', defaultMessage: 'Year of Escapes 2025 preview', description: 'Alt text for Wrapped discover modal banner image' })}
          />
        </ImageContainer>
        <ModalContent>
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage({ id: 'xe7uu+c2EE', defaultMessage: 'Take a look back at your highlights and get inspired for your next escape.', description: 'Body text encouraging user to view their Wrapped highlights' })}
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({ id: 'ki2XNxi5eL', defaultMessage: 'Discover now', description: 'Button label to view Wrapped content' }),
          onClick: handleDiscover,
        }}
      />
    </ModalBase>
  )
}

export default WrappedDiscoverModal
